<template lang="pug">
.other-user-send-message-history-tab
  .history-period.d-flex.flex-row.align-items-center
    .title.mr-2 履歴範囲
    b-form-select.history-option#other-user-history-period(v-model="historyPeriod" :options="historyPeriodOptions" size="md" @change="changeHistoryPeriod")
  .mb-2
  SendMessageHistoryTable(
    v-model="items"
    :fields="fields"
    :totalRows.sync="rows"
    :currentPage="currentPage"
    :perPage="perPage"
    :isLoading="isLoading"
    :filter="filter"
    @onDetail="showUnreadOtherUserModal"
    @onConfirm="onShowDetail"
    @onDelete="onDelete"
  )
  .table-options.d-flex.flex-row.align-items-center
    .d-flex.flex-row.justify-content-start.align-items-center
      //- 未読のみ表示
      .unread-reply-filter
        b-form-checkbox(v-model="filter.onlyUnreadReply") 未読の返信のみ表示
    .d-flex.flex-row.flex-fill.justify-content-end.align-items-center
      // 検索
      .search-filter.d-flex.mr-1.align-items-center.mr-2
        b-input-group.search-filter-input-group.mr-1(size="md")
          b-input-group-prepend
            b-input-group-text.search-filter-icon
              .search-icon
          b-form-input.search-filter-input(v-model="filter.searchWord" type="search" placeholder="検索ワードを入力してください")
        HelpIcon.d-flex.justify-content-end.align-items-center(popoverId="other-user-send-message-history-filter-help" title="検索項目について")
          template(v-slot:text)
            div 検索項目は以下になります。
            div ・宛先
            div ・タイトル
      // フィルタリング
      .d-flex.flex-row.align-items-center(v-if="showFilter == 0")
        span.table-option-text 送信者:
        .filter
          b-form-select(
            id="other-user-filter-input"
            v-model="filter.writerId"
            :options="filterOptions"
            size="md"
          )
      // ページあたりの行数
      span.table-option-text 1ページの行数:
      .per-page
        b-form-select(
          id="other-user-per-page-select"
          v-model="perPage"
          :options="pageOptions"
          size="md"
          @change="selectPerPage"
        )
      // ページネーション
      b-pagination.m-0(
        v-model="currentPage"
        :total-rows="rows"
        :per-page="perPage"
        :disabled="isLoading"
        @change="selectPage"
        hide-goto-end-buttons
      )

  SendMessageOtherUserGroupReadMembersModal(v-model="showSendMessageReadOtherUsersModal" :otherUsers="sendMessagesUnreadMembers")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Watch, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";
import { errorHandle } from "@/errorHandler";
import { PcUrl } from "@/commons/constants/PcUrl";

import Setting from "../../../../public/setting.json";
import OtherUserGroupMessagesService from "@/commons/services/OtherUserGroupMessages";
import OtherUserGroupMessageModel from "@/commons/models/OtherUserGroupMessage";
import OtherUserGroupSendMessageReadCountsService, { SendMessageReadCountsModel } from "@/commons/services/OtherUserGroupSendMessageReadCounts";
import OtherUserGroupSendMessageReadMembersService, { OtherUserGroupSendMessageReadMemberModel } from "@/commons/services/OtherUserGroupSendMessageReadMembers";
import OtherUserGroupSendMessageRepliesService, { SendMessageRepliesUnreadModel } from "@/commons/services/OtherUserGroupSendMessageReplies";
import OtherUserGroupTypesService from "@/commons/services/OtherUserGroupTypes";
import OtherUserGroupTypeModel from "@/commons/models/OtherUserGroupType";
import { FunctionType } from "@/commons/models/Message";
import StandardAccountsService, { StandardAccountsGetModel } from "@/commons/services/StandardAccounts";
import { ShowMessage } from "@/commons/services/Authorization";

import SendMessageHistoryTable, { FilterSetting, SendMessageHistoryTableField, SendMessageHistoryTableItem } from "@/pc/containers/sendMessages/SendMessageHistoryTable.vue";
import SendMessageOtherUserGroupReadMembersModal from "@/pc/components/modals/SendMessageOtherUserGroupReadMembersModal.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import HelpIcon from "@/pc/basics/HelpIcon.vue";
import SystemError from "@/commons/models/SystemError";

/**
 * メッセージ履歴コンポーネント
 */
@Component({
  components: {
    HelpIcon,
    SendMessageHistoryTable,
    SendMessageOtherUserGroupReadMembersModal,
    PageLoading
  }
})
export default class OtherUserSendMessageHistory extends Vue {
  /** メッセージサービス */
  @Inject("OtherUserGroupMessagesService") otherUserGroupMessagesService!: OtherUserGroupMessagesService;
  /** メッセージ(既読/未読)サービス */
  @Inject("OtherUserGroupSendMessageReadCountsService") otherUserGroupSendMessageReadCountsService!: OtherUserGroupSendMessageReadCountsService;
  /** 送信メッセージにひもづくメンバーサービス */
  @Inject("OtherUserGroupSendMessageReadMembersService") otherUserGroupSendMessageReadMembersService!: OtherUserGroupSendMessageReadMembersService;
  /** メッセージ送信の返信サービス */
  @Inject("OtherUserGroupSendMessageRepliesService") otherUserGroupSendMessageRepliesService!: OtherUserGroupSendMessageRepliesService;
  /** 施設関係者グループ種類サービス */
  @Inject("OtherUserGroupTypesService") otherUserGroupTypesService!: OtherUserGroupTypesService;
  /** アカウントサービス */
  @Inject("StandardAccountsService") standardAccountsService!: StandardAccountsService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";

  /** 履歴範囲 */
  historyPeriod: number = HistoryPeriodOptionValue.ONE_MONTH;
  /** 履歴範囲選択肢 */
  historyPeriodOptions: Array<{ value: number | string; text: string }> = [
    { value: HistoryPeriodOptionValue.ONE_MONTH, text: "1ヵ月" },
    { value: HistoryPeriodOptionValue.THREE_MONTH, text: "3ヵ月" },
    { value: HistoryPeriodOptionValue.SIX_MONTH, text: "6ヵ月" },
    { value: HistoryPeriodOptionValue.ONE_YEAR, text: "1年" },
    { value: HistoryPeriodOptionValue.ALL, text: "全て" }
  ];

  /** メッセージ履歴列名 */
  fields: Array<SendMessageHistoryTableField> = [];
  /** メッセージ履歴の情報 */
  items: Array<SendMessageHistoryTableItem> = [];
  /** 読込み中か否か */
  isLoading: boolean = true;

  /** 未読メンバーユーザーモーダル表示可否 */
  showSendMessageReadOtherUsersModal: boolean = false;
  /** 未読メンバーユーザーモーダルに渡すメンバー */
  sendMessagesUnreadMembers: Array<OtherUserGroupSendMessageReadMemberModalModel> = [];

  /** フィルタリングを表示するかどうか */
  showFilter: number = userStore.loginUser.permissions.showMessages;
  /** フィルタリング条件 */
  filter: FilterSetting = { searchWord: "", writerId: "", onlyUnreadReply: false };
  /** フィルター選択項目 */
  filterOptions: Array<{ value: string; text: string }> = [];
  /** ページングの現在ページ番号 */
  currentPage: number = 1;
  /** ページングの全体行数 */
  rows: number = 0;
  /** 1ページに表示する行数 */
  perPage: number = Number(Setting.sendMessage.perPage);
  /** 1ページに表示する行数の選択項目 */
  pageOptions: number[] = Setting.sendMessage.pageOptions;
  /** 先行して取得するページ範囲 */
  pageRange: number = Setting.sendMessage.pageRange;
  /** バックグラウンド取得するページ範囲 */
  bgPageRange: number = Setting.sendMessage.bgPageRange;

  /**
   * ### 1ページあたりの表示行数の変更イベント
   *
   * - 変更をクッキーに持たせる
   */
  @Watch("perPage")
  public changePerPage(perPage: number): void {
    this.setCookie(perPage);
    return;
  }

  /**
   * ### 1ページあたりの表示行数をCookieに設定する
   */
  public setCookie(perPage: number): void {
    // Cookie有効秒数。有効期限は必ず設定しないといけないので、10年分にしておく
    const saveSecond = 10 * 365 * 24 * 60 * 60;
    document.cookie = `send-message-other-user-history-per-page=${perPage}; max-age=${saveSecond};`;
    return;
  }

  /**
   *  vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    // 画面初期表示時に作成して変更しないものを初期化する
    const fields: Array<SendMessageHistoryTableField> = this.createMessageFields();
    this.fields = fields;
    // 1ページあたりの表示行数はCookieから取得する。取得できない場合はデフォルト値を使用する
    const perPage: number | null = this.loadCookie();
    this.perPage = perPage ? perPage : Number(Setting.sendMessage.perPage);

    await this.initialize();

    return;
  }

  /**
   * ### 画面初期化
   */
  public async initialize(): Promise<void> {
    this.isLoading = true;

    // 記載者フィルタリングが必要な場合はフィルタリング用のリストを作る
    if (userStore.loginUser.permissions.showMessages == ShowMessage.ALL) {
      const accounts: Array<StandardAccountsGetModel> = await this.getAccounts();
      const filterOptions: Array<{ value: string; text: string }> = await this.createFilterOptions(accounts);
      this.filterOptions = filterOptions;
    }

    this.items = [];
    this.currentPage = 1;

    await this.createView();

    this.isLoading = false;

    this.setOtherUserUnreadByCurrentPage();

    return;
  }

  /**
   * ### メッセージ履歴の列名を作成する
   */
  public createMessageFields(): Array<SendMessageHistoryTableField> {
    let fields: Array<SendMessageHistoryTableField> = [];

    fields.push({ key: "senderType", label: "送信手段", sortable: false, sortDirection: "desc", class: "sender-type text-center" });
    fields.push({ key: "datetime", label: "日時", sortable: false, sortDirection: "desc", class: "datetime text-center" });
    fields.push({ key: "receivers", label: "宛先", sortable: false, sortDirection: "desc", class: "receivers" });
    fields.push({ key: "title", label: "タイトル", sortable: false, sortDirection: "desc", class: "title" });
    fields.push({ key: "unread", label: "未読施設関係者", sortable: false, sortDirection: "desc", class: "unread" });
    fields.push({ key: "detail", label: "詳細", sortable: false, sortDirection: "desc", class: "detail text-center" });
    fields.push({ key: "writtenBy", label: "送信者", sortable: false, sortDirection: "desc", class: "written-by" });

    return fields;
  }

  /**
   * ### Cookieを見て、値が保持されている場合はCookieの値を返す
   */
  public loadCookie(): number | null {
    if (document.cookie) {
      let cookieMap: any = {};
      let cookies: Array<string> = document.cookie.split("; ");
      cookies.forEach(function (value) {
        //cookie名と値に分ける
        let content = value.split("=");
        cookieMap[content[0]] = content[1];
      });

      // cookieに値が格納されていないとundefinedが格納されているが、実際にはゴミデータが格納されているのでisNaN()でチェックが必要
      const perPage: number = cookieMap["send-message-other-user-history-per-page"];

      return isNaN(perPage) ? null : perPage;
    }

    return null;
  }

  /**
   * ### アカウントを取得する
   */
  public async getAccounts(): Promise<Array<StandardAccountsGetModel>> {
    const params: any = {
      all: "true"
    };
    const response: Array<StandardAccountsGetModel> = await this.standardAccountsService.get(userStore, params);

    return response;
  }

  /**
   * ### 記載者グループフィルター用選択項目
   */
  public createFilterOptions(accounts: Array<StandardAccountsGetModel>): Array<{ value: string; text: string }> {
    let filterOptions: Array<{ value: string; text: string }> = [];

    filterOptions.push({
      value: "",
      text: "全て"
    });

    for (const account of accounts) {
      filterOptions.push({
        value: `${account.userId}`,
        text: account.name
      });
    }

    return filterOptions;
  }

  /**
   * 画面表示情報を作成する
   */
  public async createView(): Promise<void> {
    if (this.historyPeriod == HistoryPeriodOptionValue.ALL) {
      this.setRows(undefined);

      const records: Array<OtherUserGroupMessageModel> = await this.getMessages(undefined);
      const items: Array<SendMessageHistoryTableItem> = this.createItems(records);
      this.items = items;

      this.setOtherUserUnreadByCurrentPage();
    } else {
      let today: Date = new Date();
      const end: string = this.$dateUtil.convertDateToString(today, "yyyymmdd235959000");

      switch (this.historyPeriod) {
        case HistoryPeriodOptionValue.ONE_MONTH: {
          today.setMonth(today.getMonth() - 1);
          break;
        }
        case HistoryPeriodOptionValue.THREE_MONTH: {
          today.setMonth(today.getMonth() - 3);
          break;
        }
        case HistoryPeriodOptionValue.SIX_MONTH: {
          today.setMonth(today.getMonth() - 6);
          break;
        }
        case HistoryPeriodOptionValue.ONE_YEAR: {
          today.setFullYear(today.getFullYear() - 1);
          break;
        }
        default: {
          today.setDate(today.getDate() - 7);
          break;
        }
      }
      const start: string = this.$dateUtil.convertDateToString(today, "yyyymmdd000000000");
      this.setRows({ start, end });

      const records: Array<OtherUserGroupMessageModel> = await this.getMessages({ start, end });
      const items: Array<SendMessageHistoryTableItem> = this.createItems(records);
      this.items = items;

      this.setOtherUserUnreadByCurrentPage();
    }

    return;
  }

  /**
   * ページング用の全件数を設定する
   */
  public async setRows(params: { start: string; end: string } | undefined): Promise<void> {
    try {
      let queryParams: any = {
        functionType: FunctionType.SEND_MESSAGE_OTHER_USER
      };
      if (params) {
        queryParams.start = params.start;
        queryParams.end = params.end;
      }
      const count: number = await this.otherUserGroupMessagesService.getCount(userStore, queryParams);
      this.rows = count;
    } catch (error) {
      // 本処理はawaitされない非同期処理なので、共通エラーハンドラーが例外をキャッチしない
      // 例外発生時は自分でエラーハンドラーを呼ぶ
      errorHandle(error, this);
    }

    return;
  }

  /**
   * メッセージ履歴を取得するAPI呼び出し
   */
  public async getMessages(params: { start: string; end: string } | undefined): Promise<Array<OtherUserGroupMessageModel>> {
    let response: Array<OtherUserGroupMessageModel> = [];

    try {
      let queryParams: any = {
        functionType: FunctionType.SEND_MESSAGE_OTHER_USER,
        order: "desc"
      };
      if (params) {
        queryParams.start = params.start;
        queryParams.end = params.end;
      }
      response = await this.otherUserGroupMessagesService.gets(userStore, queryParams);
    } catch (error) {
      const systemerror: SystemError = error as SystemError;
      // 413エラーの場合、全件取得した結果がペイロード制限に引っかかったことになるので、index指定で段階的に取得する
      if (systemerror.statusCode == 413) {
        const pagePerCount: number = 50;
        let queryParams: any = {
          functionType: FunctionType.SEND_MESSAGE_OTHER_USER
        };
        if (params) {
          queryParams.start = params.start;
          queryParams.end = params.end;
        }
        const count: number = await this.otherUserGroupMessagesService.getCount(userStore, queryParams);
        const loopEnd: number = Math.ceil(count / pagePerCount);

        for (let i = 0; i < loopEnd; i++) {
          queryParams.startIndex = i * pagePerCount;
          queryParams.endIndexi * pagePerCount + pagePerCount;
          queryParams.order = "desc";
          response = response.concat(await this.otherUserGroupMessagesService.gets(userStore, queryParams));
        }
      }
    }
    return response;
  }

  /**
   * ### メッセージ履歴の行を作成する
   */
  public createItems(messages: Array<OtherUserGroupMessageModel>): Array<SendMessageHistoryTableItem> {
    let items: Array<SendMessageHistoryTableItem> = [];

    for (const message of messages) {
      // 宛先文字列を作成する
      const receivers: string[] = [];
      if (message.receiverDetails) {
        for (let receiver of message.receiverDetails) {
          receivers.push(receiver.name);
        }
      }
      const successUsers: string[] = [];
      if (message.successUsers) {
        for (const successUser of message.successUsers) {
          successUsers.push(successUser.name);
        }
      }

      items.push({
        facilityId: message.facilityId,
        senderType: message.senderType,
        sortKey: message.otherUserMessagesSortKey,
        datetime: Number(message.datetime),
        receivers: receivers.join("、"),
        successUsers: successUsers.join("、"),
        functionContents: message.functionContents,
        writerId: message.writerId ? message.writerId : "",
        writtenBy: message.writtenBy ? message.writtenBy : "",
        unreadCount: undefined,
        replyUnread: undefined,
        receiverDetails: message.receiverDetails ? message.receiverDetails : []
      });
    }

    return items;
  }

  /**
   * ### 現在表示しているページ分の既読・未読件数を取得する
   */
  public async setOtherUserUnreadByCurrentPage(): Promise<void> {
    const startIndex: number = (this.currentPage - 1) * this.perPage; // 現在ページの開始インデックス
    const endIndex: number = Math.min(startIndex + this.perPage, this.items.length); // 現在ページの終了インデックス

    let requestPromises: Array<Promise<void>> = [];
    for (let i = startIndex; i < endIndex; i++) {
      if (this.items[i].unreadCount == undefined) {
        let promise: Promise<void> | null = this.setUnread(i);
        if (promise != null) {
          requestPromises.push(promise);
        }
      }
      if (this.items[i].replyUnread == undefined) {
        let promise: Promise<void> | null = this.setReplyUnread(i);
        if (promise != null) {
          requestPromises.push(promise);
        }
      }
    }

    Promise.all(requestPromises);

    return;
  }

  /**
   * ### 未読メンバーを取得する
   */
  public setUnread(no: number): Promise<void> | null {
    // 読みましたボタンが無い場合は取得処理不要
    if (this.items[no].functionContents.addReadButton === false) {
      return null;
    }

    return new Promise(async (resolve: Function, reject: Function) => {
      try {
        const message: SendMessageHistoryTableItem = this.items[no];
        const readModel: SendMessageReadCountsModel = await this.otherUserGroupSendMessageReadCountsService.get(userStore, Number(message.datetime));

        // 非同期処理中に画面遷移していた場合、このコンポーネントvueインスタンスが無くなっている可能性がある
        // 存在しない場合は何も処理しない
        if (this.items != undefined && this.items[no] != undefined) {
          this.items[no]["unreadCount"] = readModel.unreadCounts;
          this.$forceUpdate();
        }
        resolve();
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * ### 返信の未読を取得する
   */
  public setReplyUnread(no: number): Promise<void> | null {
    // 返信ボタンが無い場合は取得処理不要
    if (this.items[no].functionContents.addReplyButton === false) {
      return null;
    }

    return new Promise(async (resolve: Function, reject: Function) => {
      try {
        let message: SendMessageHistoryTableItem = this.items[no];
        const showSendMessageIncoming: SendMessageRepliesUnreadModel = await this.otherUserGroupSendMessageRepliesService.getUnread(userStore, Number(message.datetime));

        // 非同期処理中に画面遷移していた場合、このコンポーネントvueインスタンスが無くなっている可能性がある
        // 存在しない場合は何も処理しない
        if (this.items != undefined && this.items[no] != undefined) {
          this.items[no]["replyUnread"] = showSendMessageIncoming.unread;
          this.$forceUpdate();
        }
        resolve();
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * ### 1ページあたりの表示行数変更イベント
   */
  public async selectPerPage(perPage: number): Promise<void> {
    this.perPage = perPage;
    this.currentPage = 1;
    this.setOtherUserUnreadByCurrentPage();
    return;
  }

  /**
   * ### ページネーションのページ番号押下
   */
  public async selectPage(page: number): Promise<void> {
    this.currentPage = page;
    this.setOtherUserUnreadByCurrentPage();
    return;
  }

  /**
   * メッセージの詳細をモーダルで表示する
   */
  public async onShowDetail(message: SendMessageHistoryTableItem): Promise<void> {
    this.$emit("openDetail", message);
    return;
  }

  /**
   * ### 削除ボタン押下イベント
   */
  public onDelete(item: SendMessageHistoryTableItem): void {
    this.$emit("onDelete", item);
    return;
  }

  public deleteItem(deletedItem: SendMessageHistoryTableItem): void {
    this.items = this.items.concat().filter((item) => {
      if (item.sortKey == deletedItem.sortKey) {
        return false;
      } else {
        return true;
      }
    });
  }

  /**
   * ### 未読詳細を表示する
   */
  public async showUnreadOtherUserModal(message: SendMessageHistoryTableItem): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    const members: Array<OtherUserGroupSendMessageReadMemberModel> = await this.getSendMessageReadMembers(message);
    const otherUserGroupTypes: Array<OtherUserGroupTypeModel> = await this.getOtherGroupTypes();
    const otherUserGroupTypeMap: Map<number, string> = this.createOtherUserGroupTypesMap(otherUserGroupTypes);
    const unreadFamilies: Array<OtherUserGroupSendMessageReadMemberModalModel> = await this.createUnreadMembers(members, otherUserGroupTypeMap);
    this.sendMessagesUnreadMembers = unreadFamilies;

    this.isBusy = false;

    this.showSendMessageReadOtherUsersModal = true;

    return;
  }

  /**
   * ### メッセージ送信したメッセージにひもづく施設関係者を取得する
   */
  public async getSendMessageReadMembers(message: SendMessageHistoryTableItem): Promise<Array<OtherUserGroupSendMessageReadMemberModel>> {
    const otherUsers: Array<OtherUserGroupSendMessageReadMemberModel> = await this.otherUserGroupSendMessageReadMembersService.get(userStore, Number(message.datetime));
    return otherUsers;
  }

  /**
   * ### 施設関係者グループ種類を取得する
   */
  public async getOtherGroupTypes(): Promise<Array<OtherUserGroupTypeModel>> {
    const otherUserGroupTypes: Array<OtherUserGroupTypeModel> = await this.otherUserGroupTypesService.get(userStore);
    return otherUserGroupTypes;
  }

  /**
   * ### 画面表示用に施設関係者グループ種類のMAPを作成する
   */
  public createOtherUserGroupTypesMap(otherUserGroupTypes: Array<OtherUserGroupTypeModel>): Map<number, string> {
    let map: Map<number, string> = new Map<number, string>();

    for (const type of otherUserGroupTypes) {
      map.set(type.id, type.name);
    }

    return map;
  }

  /**
   * ### メッセージ送信したメッセージにひもづくメンバーから未読のメンバーを返却する
   */
  public createUnreadMembers(
    members: Array<OtherUserGroupSendMessageReadMemberModel>,
    otherUserGroupTypeMap: Map<number, string>
  ): Array<OtherUserGroupSendMessageReadMemberModalModel> {
    let unreadMembers: Array<OtherUserGroupSendMessageReadMemberModalModel> = [];

    for (let member of members) {
      if (member.readDatetime == undefined || member.readDatetime == null) {
        const _member: OtherUserGroupSendMessageReadMemberModalModel = member as OtherUserGroupSendMessageReadMemberModalModel;
        const typeName: string | undefined = otherUserGroupTypeMap.get(member.groupTypeId);
        _member.groupTypeName = typeName ? typeName : "";

        unreadMembers.push(_member);
      }
    }

    return unreadMembers;
  }

  /**
   * ### 画面の作り直し
   */
  public async refresh(): Promise<void> {
    await this.initialize();
    return;
  }

  /**
   * ### 画面の作り直し
   */
  public async refreshByNoLoading(): Promise<void> {
    try {
      await this.createView();
    } catch (error) {
      // 処理中に画面遷移が発生した場合、エラーと見なさないためURLチェックする
      if (this.$route.fullPath != PcUrl.SendMessage) {
        return;
      } else {
        throw error;
      }
    }
    return;
  }

  /**
   * ### 期間変更のセレクトボックス値変更イベント
   *
   * - 履歴を取得しなおす
   */
  public async changeHistoryPeriod(): Promise<void> {
    this.isLoading = true;

    await this.createView();

    this.isLoading = false;

    this.setOtherUserUnreadByCurrentPage();

    return;
  }
}

export enum HistoryPeriodOptionValue {
  UNKNOWN = 0,
  ONE_MONTH = 1,
  THREE_MONTH = 2,
  SIX_MONTH = 3,
  ONE_YEAR = 4,
  ALL = 5
}

interface OtherUserGroupSendMessageReadMemberModalModel extends OtherUserGroupSendMessageReadMemberModel {
  /** グループ名 */
  groupTypeName: string;
}
</script>

<style lang="scss" scoped>
.other-user-send-message-history-tab {
  .history-period {
    .title {
      font-weight: bold;
    }
    .history-option {
      width: 120px;
    }
  }
}
</style>
